/**
 * Setarile emailului de dimineata cu incasarile (spec 2026-10-07). Doar pentru admin.
 *
 * Emailul pleaca lunea la 9:00, o data pe saptamana, catre fiecare agent, cu clientii lui, iar rezumatul pe
 * echipa catre lista de mai jos. Livrat OPRIT: se porneste dupa ce adminul vede un exemplu.
 */
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { Button } from '../ui/Button'

interface Setari {
  enabled: boolean
  recipients: string[]
  lastSentAt: string | null
  agenti: string[]
}

const eroare = (e: unknown) =>
  toast.error(
    (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Nu s-a putut salva.',
  )

export function EmailIncasariCard() {
  const qc = useQueryClient()
  const q = useQuery<Setari>({
    queryKey: ['email-incasari-setari'],
    queryFn: async () => (await api.get('/agents/email-incasari/setari')).data,
  })
  const [adrese, setAdrese] = useState('')
  const [agent, setAgent] = useState('')

  useEffect(() => {
    if (q.data) setAdrese(q.data.recipients.join('\n'))
  }, [q.data])

  const salveaza = useMutation({
    mutationFn: async (corp: { enabled?: boolean; recipients?: string[] }) =>
      (await api.put('/agents/email-incasari/setari', corp)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['email-incasari-setari'] })
      toast.success('Salvat.')
    },
    onError: eroare,
  })

  const exemplu = useMutation({
    mutationFn: async () =>
      (await api.post('/agents/email-incasari/exemplu', { agent }, { timeout: 90_000 })).data as {
        trimisLa: string
      },
    onSuccess: (r) => toast.success(`Exemplul a plecat la ${r.trimisLa}.`),
    onError: eroare,
  })

  const s = q.data
  const lista = adrese
    .split(/[\s,;]+/)
    .map((x) => x.trim())
    .filter(Boolean)

  return (
    <div className="mt-6 rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-slate-800">E-mail de încasări</h2>
          <p className="text-xs text-slate-500">
            Lunea la 9:00, o dată pe săptămână: fiecare agent primește clienții lui — de sunat
            săptămâna asta, ce devine scadent în 7 zile, încasările de la emailul trecut. Agenții
            fără nimic de cerut nu primesc nimic.
            {s?.lastSentAt &&
              ` Ultimul rezumat: ${new Date(s.lastSentAt).toLocaleString('ro-RO')}.`}
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={s?.enabled ?? false}
            disabled={!s || salveaza.isPending}
            onChange={(e) => salveaza.mutate({ enabled: e.target.checked })}
          />
          {/*
            Text fix + starea separat (14.10.2026): eticheta veche era doar „Pornit"/„Oprit", iar
            „☐ Oprit" se citea ca o optiune de bifat pentru oprire — bifarea o PORNEA.
          */}
          Trimitere automată:
          <span
            className={
              s?.enabled ? 'font-semibold text-emerald-700' : 'font-semibold text-slate-500'
            }
          >
            {s?.enabled ? 'pornită' : 'oprită'}
          </span>
        </label>
      </div>

      <div className="mt-3 grid gap-4 md:grid-cols-2">
        <label className="flex flex-col gap-1 text-xs text-slate-600">
          Rezumatul de echipă merge la (o adresă pe rând)
          <textarea
            value={adrese}
            onChange={(e) => setAdrese(e.target.value)}
            rows={3}
            className="rounded border border-slate-300 px-2 py-1.5 text-sm"
            placeholder="manager.vanzari@boost4life.ro"
          />
          <span className="text-[11px] text-slate-500">
            Rezumatul arată toată echipa, inclusiv clienții pe care unii manageri nu-i văd în
            tablou. Pune aici doar cine trebuie să vadă tot.
          </span>
          <div>
            <Button
              variant="secondary"
              loading={salveaza.isPending}
              onClick={() => salveaza.mutate({ recipients: lista })}
            >
              Salvează adresele
            </Button>
          </div>
        </label>

        <label className="flex flex-col gap-1 text-xs text-slate-600">
          Trimite-mi un exemplu acum — e-mailul unui agent și rezumatul, doar pe adresa ta
          <select
            value={agent}
            onChange={(e) => setAgent(e.target.value)}
            className="rounded border border-slate-300 px-2 py-1.5 text-sm"
          >
            <option value="">— alege agentul —</option>
            {(s?.agenti ?? []).map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
          <span className="text-[11px] text-slate-500">
            Exemplul nu oprește e-mailurile reale ale zilei.
          </span>
          <div>
            <Button disabled={!agent} loading={exemplu.isPending} onClick={() => exemplu.mutate()}>
              Trimite-mi exemplul
            </Button>
          </div>
        </label>
      </div>
    </div>
  )
}
